import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, MenuItem, Snackbar, Stack, Switch,
    Tab, Tabs, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    DashboardAppSettingDto, getBetupFeedChannels, getChannelArticleExecutions, getPublishingPosts, listDashboardAppSettings,
    putDashboardAppSetting,
} from '../Api';
import { BetupFeedChannelDto } from '../models/dtos/BetupFeedChannelDto';
import {
    CHANNEL_ARTICLE_SETTING_KEYS, CHANNEL_ARTICLE_STAGES, CHANNEL_ARTICLE_WORKFLOW, ChannelArticleExecutionDto,
    ChannelArticleExecutionPageDto, ChannelArticleStatus, PublishingPostDto, PublishingPostStatus,
} from '../models/dtos/PublishingDto';
import ChannelArticleExecutionDialog, { ARTICLE_STAGE_LABELS, ArticleStageChip, publishingMatchTitle } from '../components/publishing/ChannelArticleExecutionDialog';
import WriteArticleDialog from '../components/publishing/WriteArticleDialog';
import PublishingPostDialog from '../components/publishing/PublishingPostDialog';
import { channelName } from '../components/betupFeed/BetupFeedChannelDialogs';
import { formatAiDuration, formatAiUsd } from '../components/ai/AiShared';

interface SettingsForm {
    enabled: boolean;
    channelIds: number[];
    autoPublish: boolean;
    windowHours: string;
    minBets: string;
    intervalMinutes: string;
    maxArticlesPerRun: string;
    maxPerChannel24h: string;
    maxFailures24h: string;
}

type NumberKey = 'windowHours' | 'minBets' | 'intervalMinutes' | 'maxArticlesPerRun' | 'maxPerChannel24h' | 'maxFailures24h';

type Summary = Pick<ChannelArticleExecutionPageDto, 'failures_24h' | 'max_failures_24h' | 'running'>;

const DEFAULTS: SettingsForm = {
    enabled: false, channelIds: [], autoPublish: false, windowHours: '36', minBets: '100', intervalMinutes: '120',
    maxArticlesPerRun: '3', maxPerChannel24h: '2', maxFailures24h: '3',
};

const NUMBER_FIELDS: { key: NumberKey; label: string; help: string; min: number; max: number }[] = [
    { key: 'windowHours', label: 'Window (hours)', help: 'Matches starting within this many hours are considered.', min: 1, max: 168 },
    { key: 'minBets', label: 'Min bets', help: 'Matches with fewer bets are skipped.', min: 0, max: 1_000_000 },
    { key: 'intervalMinutes', label: 'Interval (minutes)', help: 'Time between two scheduled runs; checked every 5 minutes.', min: 5, max: 1440 },
    { key: 'maxArticlesPerRun', label: 'Max articles per run', help: 'Triggers per scheduled run, one per channel, queued and running ones counted.', min: 0, max: 50 },
    { key: 'maxPerChannel24h', label: 'Max per channel in 24 h', help: 'AI articles a channel gets in 24 hours, drafts included, before the scheduler skips it.', min: 0, max: 50 },
    { key: 'maxFailures24h', label: 'Max failures in 24 h', help: 'Once this many scheduled invocations failed in 24 hours, the scheduler sends nothing. Manual triggers neither count nor stop.', min: 0, max: 1000 },
];

const SETTING_KEY: Record<NumberKey, string> = {
    windowHours: CHANNEL_ARTICLE_SETTING_KEYS.windowHours,
    minBets: CHANNEL_ARTICLE_SETTING_KEYS.minBets,
    intervalMinutes: CHANNEL_ARTICLE_SETTING_KEYS.intervalMinutes,
    maxArticlesPerRun: CHANNEL_ARTICLE_SETTING_KEYS.maxArticlesPerRun,
    maxPerChannel24h: CHANNEL_ARTICLE_SETTING_KEYS.maxPerChannel24h,
    maxFailures24h: CHANNEL_ARTICLE_SETTING_KEYS.maxFailures24h,
};

function formFromSettings(rows: DashboardAppSettingDto[]): SettingsForm {
    const byKey = new Map(rows.map(r => [r.key, r]));
    const decimal = (key: NumberKey) => {
        const v = byKey.get(SETTING_KEY[key])?.decimalValue;
        return v == null ? DEFAULTS[key] : String(v);
    };
    const ids = byKey.get(CHANNEL_ARTICLE_SETTING_KEYS.channelIds)?.stringValue;
    return {
        enabled: byKey.get(CHANNEL_ARTICLE_SETTING_KEYS.enabled)?.booleanValue ?? DEFAULTS.enabled,
        channelIds: ids == null ? DEFAULTS.channelIds : ids.split(/[,\s]+/).map(Number).filter(n => Number.isInteger(n) && n > 0),
        autoPublish: byKey.get(CHANNEL_ARTICLE_SETTING_KEYS.autoPublish)?.booleanValue ?? DEFAULTS.autoPublish,
        windowHours: decimal('windowHours'),
        minBets: decimal('minBets'),
        intervalMinutes: decimal('intervalMinutes'),
        maxArticlesPerRun: decimal('maxArticlesPerRun'),
        maxPerChannel24h: decimal('maxPerChannel24h'),
        maxFailures24h: decimal('maxFailures24h'),
    };
}

type PublishingTab = 'drafts' | 'all' | 'invocations';

/**
 * BETUP Feed › Publishing (betup-server-api docs/channel-articles.md §9): the AI channel articles' settings (generic
 * `channel_article.*` app settings read by Crons on every run; they limit the scheduler only), Run now (one manual
 * trigger), drafts and all channel posts across channels, and every article invocation with its AI workflow run.
 * `?channel=<id>` / `?match=<id>` narrow the lists.
 */
export default function PublishingPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tab: PublishingTab = tabParam === 'all' || tabParam === 'invocations' ? tabParam : 'drafts';
    const channelFilter = Number(searchParams.get('channel')) || null;
    const matchFilter = Number(searchParams.get('match')) || null;
    const setParam = (changes: Record<string, string | null>) => setSearchParams(current => {
        const next = new URLSearchParams(current);
        Object.entries(changes).forEach(([key, value]) => value == null ? next.delete(key) : next.set(key, value));
        return next;
    }, { replace: true });

    const [form, setForm] = React.useState<SettingsForm>(DEFAULTS);
    const [saved, setSaved] = React.useState<SettingsForm>(DEFAULTS);
    const [settingsError, setSettingsError] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [channels, setChannels] = React.useState<BetupFeedChannelDto[]>([]);
    const [toast, setToast] = React.useState<string | null>(null);
    const [runNowOpen, setRunNowOpen] = React.useState(false);
    const [summary, setSummary] = React.useState<Summary | null>(null);
    const [version, setVersion] = React.useState(0);

    React.useEffect(() => {
        listDashboardAppSettings('channel_article.').then(result => {
            if (result == null) {
                setSettingsError('The settings could not be loaded; the defaults are shown.');
                return;
            }
            const loaded = formFromSettings(result);
            setForm(loaded);
            setSaved(loaded);
        });
        getBetupFeedChannels().then(result => setChannels(result ?? []));
    }, []);

    const dirty = JSON.stringify(form) !== JSON.stringify(saved);
    const channelById = React.useMemo(() => new Map(channels.map(c => [c.id, c])), [channels]);
    const nameOf = (id: number) => { const c = channelById.get(id); return c ? channelName(c) : `#${id}`; };

    const save = async () => {
        for (const f of NUMBER_FIELDS) {
            const n = Number(form[f.key]);
            if (form[f.key].trim() === '' || !Number.isInteger(n) || n < f.min || n > f.max) {
                setSettingsError(`${f.label} must be a whole number from ${f.min} to ${f.max}.`);
                return;
            }
        }
        setSaving(true);
        setSettingsError(null);
        type Write = [string, Parameters<typeof putDashboardAppSetting>[1]];
        const writes: Write[] = [
            [CHANNEL_ARTICLE_SETTING_KEYS.enabled, { valueType: 'BOOLEAN', booleanValue: form.enabled }],
            [CHANNEL_ARTICLE_SETTING_KEYS.autoPublish, { valueType: 'BOOLEAN', booleanValue: form.autoPublish }],
            [CHANNEL_ARTICLE_SETTING_KEYS.channelIds, { valueType: 'STRING', stringValue: form.channelIds.join(',') }],
            ...NUMBER_FIELDS.map((f): Write => [SETTING_KEY[f.key], { valueType: 'DECIMAL', decimalValue: Number(form[f.key]) }]),
        ];
        for (const [key, body] of writes) {
            const { error } = await putDashboardAppSetting(key, body);
            if (error) {
                setSettingsError(`${key}: ${error}`);
                setSaving(false);
                return;
            }
        }
        setSaved(form);
        setSaving(false);
        setToast('Settings saved; Crons uses them on its next run');
    };

    const stopped = summary != null && summary.failures_24h >= summary.max_failures_24h;
    const reload = () => setVersion(v => v + 1);

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" alignItems="flex-start" gap={2}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4">Publishing</Typography>
                    <Typography variant="body2" color="text.secondary">
                        AI articles for the channels. Each trigger is one invocation in Crons, one at a time: it picks a channel and
                        an upcoming match, reuses the match's fresh web research or runs the researcher, writes in the channel's
                        voice with widgets, translates into its languages and saves a draft — or publishes it with auto-publish.
                        Every AI call of an invocation shares its correlation id.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setRunNowOpen(true)}>Run now</Button>
            </Stack>

            {stopped && (
                <Alert severity="warning">
                    {summary!.failures_24h} scheduled invocations failed in the last 24 hours (limit {summary!.max_failures_24h}): the
                    scheduler sends no new trigger until the count drops or the limit is raised. Manual triggers still work.
                </Alert>
            )}

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6">Settings</Typography>
                        <Button variant="contained" startIcon={<SaveIcon />} disabled={!dirty || saving} onClick={save}>Save</Button>
                    </Stack>
                    {settingsError && <Alert severity="warning" sx={{ mb: 2 }}>{settingsError}</Alert>}
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />}
                                label={form.enabled ? 'Scheduler on' : 'Scheduler off'} />
                            <Typography variant="caption" color="text.secondary" display="block">
                                Off: the scheduler sends no trigger. Manual triggers (Run now, the channel and match pages) still work.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControlLabel
                                control={<Switch checked={form.autoPublish} onChange={e => setForm({ ...form, autoPublish: e.target.checked })} />}
                                label={form.autoPublish ? 'Auto-publish on' : 'Drafts only'} />
                            <Typography variant="caption" color="text.secondary" display="block">
                                On: every article (manual ones too) goes out within a minute, with its push to subscribers. Off: drafts to review here.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                options={channels.map(c => c.id)}
                                value={form.channelIds}
                                onChange={(_, value) => setForm({ ...form, channelIds: value })}
                                getOptionLabel={id => `${nameOf(id)} (${id})`}
                                renderInput={params => <TextField {...params} size="small" label="Channels"
                                    helperText={form.channelIds.length === 0
                                        ? 'None chosen: every active channel with sports.'
                                        : 'The scheduler writes for these channels (active ones with sports).'} />}
                            />
                        </Grid>
                        {NUMBER_FIELDS.map(f => (
                            <Grid item xs={12} sm={6} md={2} key={f.key}>
                                <TextField size="small" fullWidth label={f.label} value={form[f.key]} helperText={f.help}
                                    onChange={e => setForm({ ...form, [f.key]: e.target.value.replace(/[^\d]/g, '') })} />
                            </Grid>
                        ))}
                    </Grid>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Fresh research is reused by Matches › AI predictions' "Reuse research (hours)" setting, shared by both workflows.
                    </Typography>
                </CardContent>
            </Card>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <Tabs value={tab} onChange={(_, v) => setParam({ tab: v === 'drafts' ? null : v })} sx={{ flex: 1 }}>
                        <Tab value="drafts" label="Drafts" />
                        <Tab value="all" label="All posts" />
                        <Tab value="invocations" label={summary?.running ? `Invocations (${summary.running} running)` : 'Invocations'} />
                    </Tabs>
                    {channelFilter && <Chip label={`Only ${nameOf(channelFilter)}`} onDelete={() => setParam({ channel: null })} />}
                    {matchFilter && <Chip label={`Only match ${matchFilter}`} onDelete={() => setParam({ match: null })} />}
                </Stack>
            </Box>
            {tab === 'invocations'
                ? <InvocationsTable version={version} onSummary={setSummary} channelId={channelFilter} matchId={matchFilter} channelById={channelById} onChanged={reload} onToast={setToast} />
                : <PostsTable key={tab} status={tab === 'drafts' ? 'draft' : 'all'} version={version} channelId={channelFilter} matchId={matchFilter} channelById={channelById}
                    onChanged={message => { setToast(message); reload(); }} />}

            <WriteArticleDialog open={runNowOpen} channels={channels} channelId={null} matchId={null} onClose={() => setRunNowOpen(false)}
                onQueued={(channelId, matchId) => {
                    setRunNowOpen(false);
                    setToast(`Queued: an article${channelId ? ` for ${nameOf(channelId)}` : ''}${matchId ? ` about match ${matchId}` : ''}`);
                    setTimeout(reload, 1500);
                }} />
            <Snackbar open={toast != null} autoHideDuration={2500} message={toast} onClose={() => setToast(null)} />
        </Stack>
    );
}

/** Channel posts across channels, newest first: drafts to review, or every post; each opens with its card and editor. */
function PostsTable({ status, version, channelId, matchId, channelById, onChanged }: {
    status: PublishingPostStatus;
    version: number;
    channelId: number | null;
    matchId: number | null;
    channelById: Map<number, BetupFeedChannelDto>;
    onChanged: (message: string) => void;
}) {
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [stateFilter, setStateFilter] = React.useState<PublishingPostStatus>(status);
    const [ai, setAi] = React.useState<'' | 'true' | 'false'>('');
    const [rows, setRows] = React.useState<PublishingPostDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [open, setOpen] = React.useState<PublishingPostDto | null>(null);
    const [executionId, setExecutionId] = React.useState<number | null>(null);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getPublishingPosts(paginationModel.page * paginationModel.pageSize, paginationModel.pageSize, stateFilter,
            { channelId, matchId, ai: ai === '' ? null : ai === 'true' });
        setFailed(page == null);
        setRows(page?.items ?? []);
        setRowCount(page?.totalItems ?? 0);
        setLoading(false);
    }, [paginationModel, stateFilter, channelId, matchId, ai]);

    React.useEffect(() => { load(); }, [load, version]);

    const columns: GridColDef<PublishingPostDto>[] = [
        { field: 'created_at', headerName: 'Created', width: 160, sortable: false, valueGetter: p => new Date(p.row.created_at).toLocaleString() },
        {
            field: 'channel', headerName: 'Channel', width: 170, sortable: false,
            renderCell: p => <Typography variant="body2" noWrap>{p.row.channel_name ?? `#${p.row.channel_id}`}</Typography>,
        },
        {
            field: 'match', headerName: 'Match', width: 220, sortable: false,
            renderCell: p => p.row.linked_match
                ? (
                    <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                        <Typography variant="body2">{publishingMatchTitle(p.row.linked_match)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {p.row.linked_match.start ? new Date(p.row.linked_match.start).toLocaleString() : `match ${p.row.linked_match.id}`}
                        </Typography>
                    </Box>
                )
                : '—',
        },
        {
            field: 'state', headerName: 'State', width: 150, sortable: false,
            renderCell: p => (
                <Stack direction="row" gap={0.5} alignItems="center">
                    <Chip size="small" variant={p.row.draft ? 'outlined' : 'filled'} color={p.row.draft ? (p.row.will_publish_at ? 'info' : 'default') : 'success'}
                        label={p.row.draft ? (p.row.will_publish_at ? 'Scheduled' : 'Draft') : 'Published'} />
                    {p.row.ai_execution_id && <Chip size="small" icon={<AutoAwesomeIcon />} label="AI" variant="outlined" color="secondary"
                        onClick={e => { e.stopPropagation(); setExecutionId(p.row.ai_execution_id); }} />}
                </Stack>
            ),
        },
        {
            field: 'text', headerName: 'Text', flex: 1, minWidth: 260, sortable: false,
            renderCell: p => <Typography variant="body2" noWrap title={p.row.text}>{firstLine(p.row.text)}</Typography>,
        },
        { field: 'languages', headerName: 'Lang.', width: 110, sortable: false, valueGetter: p => Object.keys(p.row.texts ?? {}).join(', ') },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {status === 'draft'
                        ? 'Drafts waiting for review, newest first: open one to read it in every language, edit, publish or delete it.'
                        : 'Every channel post, newest first. AI marks the ones an article invocation wrote.'}
                </Typography>
                {status !== 'draft' && (
                    <TextField select size="small" label="State" value={stateFilter} sx={{ width: 150 }}
                        onChange={e => { setStateFilter(e.target.value as PublishingPostStatus); setPaginationModel(m => ({ ...m, page: 0 })); }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="draft">Drafts</MenuItem>
                        <MenuItem value="scheduled">Scheduled</MenuItem>
                        <MenuItem value="published">Published</MenuItem>
                    </TextField>
                )}
                <TextField select size="small" label="Author" value={ai} sx={{ width: 150 }}
                    onChange={e => { setAi(e.target.value as '' | 'true' | 'false'); setPaginationModel(m => ({ ...m, page: 0 })); }}>
                    <MenuItem value="">Anyone</MenuItem>
                    <MenuItem value="true">AI articles</MenuItem>
                    <MenuItem value="false">People</MenuItem>
                </TextField>
                <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
            </Stack>
            {failed && <Alert severity="error">The posts could not be loaded.</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                rowCount={rowCount}
                loading={loading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowHeight={() => 'auto'}
                onRowClick={p => setOpen(p.row)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 0.5 } }}
                localeText={{ noRowsLabel: status === 'draft' ? 'No drafts to review' : 'No posts' }}
            />
            <PublishingPostDialog post={open} channel={open ? channelById.get(open.channel_id) ?? null : null} onClose={() => setOpen(null)}
                onChanged={message => onChanged(message)} />
            <ChannelArticleExecutionDialog executionId={executionId} onClose={() => setExecutionId(null)} />
        </Stack>
    );
}

/** Article invocations newest first (or by status), refreshed every 10 s while one runs. */
function InvocationsTable({ version, onSummary, channelId, matchId, channelById, onChanged, onToast }: {
    version: number;
    onSummary: (summary: Summary) => void;
    channelId: number | null;
    matchId: number | null;
    channelById: Map<number, BetupFeedChannelDto>;
    onChanged: () => void;
    onToast: (message: string) => void;
}) {
    const [status, setStatus] = React.useState<ChannelArticleStatus | ''>('');
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 20 });
    const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
    const [page, setPage] = React.useState<ChannelArticleExecutionPageDto | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [openId, setOpenId] = React.useState<number | null>(null);
    const [post, setPost] = React.useState<PublishingPostDto | null>(null);

    const load = React.useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        const sort = sortModel[0]?.field === 'status' ? 'status' : 'created';
        const direction = sortModel[0]?.sort === 'asc' ? 'asc' : 'desc';
        const result = await getChannelArticleExecutions(paginationModel.page * paginationModel.pageSize, paginationModel.pageSize,
            status || null, sort, direction, { channelId, matchId });
        setFailed(result == null);
        if (result) {
            setPage(result);
            onSummary({ failures_24h: result.failures_24h, max_failures_24h: result.max_failures_24h, running: result.running });
        }
        setLoading(false);
    }, [paginationModel, sortModel, status, onSummary, channelId, matchId]);

    React.useEffect(() => { load(); }, [load, version]);

    React.useEffect(() => {
        if (!page?.running) return;
        const timer = setInterval(() => load(true), 10_000);
        return () => clearInterval(timer);
    }, [page?.running, load]);

    const openPost = async (postId: number, postChannelId: number) => {
        const found = await getPublishingPosts(0, 50, 'all', { channelId: postChannelId });
        const match = found?.items.find(p => p.id === postId) ?? null;
        if (match) { setOpenId(null); setPost(match); } else onToast(`Post #${postId} is not among the channel's latest posts (deleted?)`);
    };

    const columns: GridColDef<ChannelArticleExecutionDto>[] = [
        { field: 'created_at', headerName: 'Received', width: 170, valueGetter: p => new Date(p.row.created_at).toLocaleString() },
        {
            field: 'source', headerName: 'Source', width: 160, sortable: false,
            renderCell: p => (
                <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                    <Typography variant="body2">{p.row.source === 'MANUAL' ? 'Manual' : 'Scheduler'}</Typography>
                    {p.row.requested_by && <Typography variant="caption" color="text.secondary">{p.row.requested_by}</Typography>}
                </Box>
            ),
        },
        {
            field: 'channel', headerName: 'Channel', width: 160, sortable: false,
            valueGetter: p => p.row.channel_name ?? (p.row.requested_channel_id ? `#${p.row.requested_channel_id}` : '—'),
        },
        {
            field: 'match', headerName: 'Match', flex: 1, minWidth: 200, sortable: false,
            renderCell: p => p.row.match
                ? (
                    <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
                        <Typography variant="body2">{publishingMatchTitle(p.row.match)}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.row.match.league ?? `match ${p.row.match.id}`}</Typography>
                    </Box>
                )
                : <Typography variant="body2" color="text.secondary">{p.row.requested_match_id ? `match ${p.row.requested_match_id}` : '—'}</Typography>,
        },
        {
            field: 'status', headerName: 'Status', width: 210,
            renderCell: p => (
                <Stack direction="row" gap={0.5} alignItems="center">
                    <ArticleStageChip status={p.row.status} />
                    {p.row.failed_stage && <Typography variant="caption" color="error">at {ARTICLE_STAGE_LABELS[p.row.failed_stage]}</Typography>}
                </Stack>
            ),
        },
        {
            field: 'outcome', headerName: 'Note / error', flex: 1, minWidth: 200, sortable: false,
            renderCell: p => {
                const text = p.row.error ?? p.row.note;
                return text
                    ? <Typography variant="body2" color={p.row.error ? 'error' : 'text.secondary'} noWrap title={text}>{text}</Typography>
                    : '—';
            },
        },
        { field: 'duration', headerName: 'Duration', width: 100, sortable: false, valueGetter: p => p.row.finished_at ? formatAiDuration(p.row.created_at, p.row.finished_at) : '…' },
        { field: 'cost_usd', headerName: 'AI cost', width: 100, sortable: false, valueGetter: p => p.row.cost_usd == null ? '—' : formatAiUsd(p.row.cost_usd) },
        {
            field: 'correlation_id', headerName: 'AI run', width: 170, sortable: false,
            renderCell: p => p.row.correlation_id
                ? <Button size="small" component={RouterLink} onClick={e => e.stopPropagation()} sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                    to={`/ai/workflows/${CHANNEL_ARTICLE_WORKFLOW}/runs/${encodeURIComponent(p.row.correlation_id)}`}>{p.row.correlation_id}</Button>
                : '—',
        },
    ];

    return (
        <Stack gap={1.5}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    Every article trigger taken by Crons, newest first. Sort by Status to group them by stage. Open one for its stages and AI calls.
                </Typography>
                <TextField select size="small" label="Status" value={status} sx={{ width: 200 }}
                    onChange={e => { setStatus(e.target.value as ChannelArticleStatus | ''); setPaginationModel(m => ({ ...m, page: 0 })); }}>
                    <MenuItem value="">All</MenuItem>
                    {CHANNEL_ARTICLE_STAGES.map(s => <MenuItem key={s} value={s}>{ARTICLE_STAGE_LABELS[s]}</MenuItem>)}
                </TextField>
                <Button startIcon={<RefreshIcon />} onClick={() => load()}>Refresh</Button>
            </Stack>
            {failed && <Alert severity="error">The invocations could not be loaded.</Alert>}
            <DataGrid
                autoHeight
                rows={page?.items ?? []}
                columns={columns}
                rowCount={page?.totalItems ?? 0}
                loading={loading}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortingMode="server"
                sortModel={sortModel}
                onSortModelChange={model => setSortModel(model.length ? model : [{ field: 'created_at', sort: 'desc' }])}
                pageSizeOptions={[20, 50, 100]}
                disableRowSelectionOnClick
                disableColumnMenu
                getRowHeight={() => 'auto'}
                onRowClick={p => setOpenId(p.row.id)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { py: 0.5 } }}
                localeText={{ noRowsLabel: 'No article invocations yet: switch the scheduler on or use Run now' }}
            />
            <ChannelArticleExecutionDialog executionId={openId} onClose={() => setOpenId(null)} onOpenPost={openPost} />
            <PublishingPostDialog post={post} channel={post ? channelById.get(post.channel_id) ?? null : null} onClose={() => setPost(null)}
                onChanged={message => { onToast(message); onChanged(); }} />
        </Stack>
    );
}

/** The first line of a post text that isn't a widget fence or its JSON. */
function firstLine(text: string): string {
    let inBlock = false;
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (line.startsWith('```')) {
            if (!(line.length > 3 && line.endsWith('```') && line.includes('{'))) inBlock = !inBlock;
            continue;
        }
        if (!inBlock && line) return line;
    }
    return '—';
}
